import { useCallback, useEffect, useState } from "react";

export type VatMode = "excl" | "incl";

const STORAGE_KEY = "elmarknad_vat_mode";
// Spotpriser specificeras alltid exkl. moms som standard. Endast jämförpris /
// totalpris i sökresultatet avviker och visas inkl. moms (prisspecifikationen
// visar rådata exkl. moms + momsrad).
const DEFAULT: VatMode = "excl";

let stored: VatMode = DEFAULT;
/** Tillfälligt läge (t.ex. inkl. moms i sökresultat) som inte sparas. */
let override: VatMode | null = null;
const listeners = new Set<(m: VatMode) => void>();

function read(): VatMode {
  if (typeof window === "undefined") return DEFAULT;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "incl" || v === "excl" ? v : DEFAULT;
}

const effective = () => override ?? stored;
const emit = () => {
  const m = effective();
  listeners.forEach((l) => l(m));
};

/** Sätt/rensa ett tillfälligt momsläge utan att skriva över besökarens val. */
export function setVatOverride(mode: VatMode | null) {
  if (override === mode) return;
  override = mode;
  emit();
}

/**
 * Global (cross-component) toggle between prices excl. and incl. moms.
 * Persisted in localStorage so the choice follows the visitor across pages.
 */
export function useVatMode() {
  const [vatMode, setVatModeState] = useState<VatMode>(effective());

  useEffect(() => {
    // Read persisted value after hydration to avoid SSR mismatches.
    const persisted = read();
    if (persisted !== stored) stored = persisted;
    setVatModeState(effective());

    const listener = (m: VatMode) => setVatModeState(m);
    listeners.add(listener);

    // Keep state in sync across tabs/windows via localStorage.
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next = read();
      if (next !== stored) {
        stored = next;
        emit();
      }
    };
    window.addEventListener("storage", handleStorage);

    return () => {
      listeners.delete(listener);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const setVatMode = useCallback((m: VatMode) => {
    // Ett aktivt val från besökaren vinner alltid över tillfälligt läge.
    override = null;
    stored = m;
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, m);
    }
    emit();
  }, []);

  return {
    vatMode,
    setVatMode,
    includeVat: vatMode === "incl",
    /** Multiplicera ett pris exkl. moms med denna faktor */
    vatFactor: vatMode === "incl" ? 1.25 : 1,
  };
}
